import React, { useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Axios from "axios";
import { useSignOut } from "react-auth-kit";
import { useNavigate, useLocation } from "react-router-dom";
import { tokens } from "../../theme";
import { getAuthHeader } from "../../utils/authToken";
import Loading from "../../components/Loading";

const TEST_TYPE_LABELS = {
    0: "SAT",
    1: "ACT",
    2: "AP",
    3: "PSAT",
    10: "Weekly SAT",
    11: "Weekly ACT",
    12: "Weekly AP",
    20: "Half SAT",
    21: "Half ACT",
    22: "Half AP",
    23: "Half PSAT",
};

const ETTReports = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const signOut = useSignOut();
    const navigate = useNavigate();
    const location = useLocation();

    const [searchEmail, setSearchEmail] = useState(location.state?.searchEmail || "");
    const [students, setStudents] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(location.state?.selectedStudent || null);
    const [testSessions, setTestSessions] = useState(location.state?.testSessions || []);
    const [loading, setLoading] = useState(false);
    const [searchPerformed, setSearchPerformed] = useState(!!location.state?.selectedStudent);

    const handleLogout = () => {
        signOut();
        localStorage.removeItem("userName");
        localStorage.removeItem("who");
        navigate("/signin");
    };

    const handleSearch = async () => {
        if (!searchEmail.trim()) return;

        setLoading(true);
        setSearchPerformed(true);
        setSelectedStudent(null);
        setTestSessions([]);

        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/ett-reports/search-users`,
                {
                    params: { email: searchEmail.trim() },
                    headers: { Authorization: getAuthHeader() },
                }
            );
            setStudents(response.data);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                console.error("Search error:", error);
                setStudents([]);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSelectStudent = async (student) => {
        setSelectedStudent(student);
        setLoading(true);

        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/ett-reports/users/${student.id}/test-sessions`,
                {
                    headers: { Authorization: getAuthHeader() },
                }
            );
            setTestSessions(response.data);
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                console.error("Fetch sessions error:", error);
                setTestSessions([]);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        setSelectedStudent(null);
        setTestSessions([]);
    };

    const handleViewReport = (session) => {
        navigate(`/admin/ettReports/${selectedStudent.id}/${session.test_id}`, {
            state: { selectedStudent, testSessions, searchEmail, localStudent: selectedStudent.localStudent || null },
        });
    };

    const getScoreDisplay = (session) => {
        const type = session.test_type;
        if (type === 1) {
            // ACT: composite
            const scores = [session.score_1, session.score_2, session.score_3, session.score_4].filter(s => s > 0);
            const composite = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
            return `${composite} (E:${session.score_1} M:${session.score_2} R:${session.score_3} S:${session.score_4})`;
        }
        // SAT/PSAT: total of two sections
        const total = (session.score_1 || 0) + (session.score_2 || 0);
        return `${total} (RW:${session.score_1} M:${session.score_2})`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    localStorage.setItem("selectedMenu", "ettReports");

    if (loading) return <Loading />;

    return (
        <Box m="20px">
            <Typography variant="h4" fontWeight="bold" mb="20px">
                ETT Exam Reports
            </Typography>

            {!selectedStudent ? (
                // Search & student list view
                <Box>
                    <Box display="flex" gap="10px" mb="20px" alignItems="flex-start">
                        <TextField
                            label="Search student by ETT email"
                            variant="outlined"
                            size="small"
                            value={searchEmail}
                            onChange={(e) => setSearchEmail(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            sx={{ width: "400px" }}
                            helperText="Also accepts consultation number, Universe email, or name"
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SearchIcon />}
                            onClick={handleSearch}
                        >
                            Search
                        </Button>
                    </Box>

                    {searchPerformed && students.length === 0 && (
                        <Typography color={colors.grey[300]}>No students found.</Typography>
                    )}

                    {students.length > 0 && (
                        <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[600] }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Email</TableCell>
                                        <TableCell>First Name</TableCell>
                                        <TableCell>Last Name</TableCell>
                                        <TableCell>Grade</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {students.map((student) => (
                                        <TableRow
                                            key={student.id}
                                            hover
                                            sx={{ cursor: "pointer" }}
                                            onClick={() => handleSelectStudent(student)}
                                        >
                                            <TableCell>
                                                {student.email}
                                                {student.localStudent && (
                                                    <Chip
                                                        label={`${student.localStudent.first_name} ${student.localStudent.last_name}`}
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                        sx={{ ml: 1 }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>{student.first_name}</TableCell>
                                            <TableCell>{student.last_name}</TableCell>
                                            <TableCell>{student.grade || "N/A"}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            ) : (
                // Selected student - test sessions view
                <Box>
                    <Box display="flex" alignItems="center" gap="15px" mb="20px">
                        <IconButton onClick={handleBack}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography variant="h5" fontWeight="bold">
                                {selectedStudent.first_name} {selectedStudent.last_name}
                            </Typography>
                            <Typography variant="body2" color={colors.grey[300]}>
                                {selectedStudent.email}
                                {selectedStudent.grade && ` | Grade ${selectedStudent.grade}`}
                            </Typography>
                        </Box>
                    </Box>

                    {testSessions.length === 0 ? (
                        <Typography color={colors.grey[300]}>No finished exams found.</Typography>
                    ) : (
                        <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[600] }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Test Name</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Score</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {testSessions.map((session) => (
                                        <TableRow key={session.id} hover>
                                            <TableCell>{session.test_title}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={TEST_TYPE_LABELS[session.test_type] || `Type ${session.test_type}`}
                                                    size="small"
                                                    color="primary"
                                                />
                                            </TableCell>
                                            <TableCell>{getScoreDisplay(session)}</TableCell>
                                            <TableCell>{formatDate(session.finished_at)}</TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleViewReport(session)}
                                                >
                                                    View Report
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ETTReports;
